import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
    Alert, Autosuggest, Box, Button, FormField, Link, Modal, Select, SpaceBetween, Spinner,
} from '@amzn/awsui-components-react';
import { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import { AutosuggestProps } from '@amzn/awsui-components-react/polaris/autosuggest';
import CONSTANTS from 'src/utils/constants';
import { CommonBalancePoolColumn } from 'src/models/multidimensional-allocation/CommonBalancePool';
import { CommonBalancePoolBuilderContext } from 'src/services/multidimensional-allocation/common-balance-pool/CommonBalancePoolBuilderContext';
import { ACTIONS } from 'src/services/multidimensional-allocation/common-balance-pool/CommonBalancePoolBuilderReducer';
import { sourceKeyOf } from './cbpInputs';
import { withNames } from './cbpMessages';

const SEGMENT_CDT_OPTION: SelectProps.Option = { label: 'Segmented CDT', value: 'SEGMENT_CDT' };
const MDA_OUTPUT_OPTION: SelectProps.Option = { label: 'MDA allocation output', value: 'MDA_OUTPUT' };

/**
 * Segmented CDT is the only input type offered. Materialisation reads SEGMENT_CDT sources only, so an MDA
 * allocation output input would be accepted here and then contribute nothing to the pool.
 */
const INPUT_TYPE_OPTIONS: SelectProps.Option[] = [SEGMENT_CDT_OPTION];

/**
 * The options for the input being added or edited. An input already saved as an MDA allocation output keeps
 * that option while it is edited, so opening it does not silently re-type it as a segmented CDT on save.
 */
const inputTypeOptionsFor = (sourceType?: string): SelectProps.Option[] =>
    (sourceType === MDA_OUTPUT_OPTION.value ? [SEGMENT_CDT_OPTION, MDA_OUTPUT_OPTION] : INPUT_TYPE_OPTIONS);

/** Normalise a grain/segment string for comparison (strip case, spaces, underscores). */
const normSegment = (s?: string): string => (s ?? '').replace(/[^a-z0-9]/gi, '').toUpperCase();

/** The UI label for a CBP grain, e.g. COST_CENTER -> "Cost Center" (LEGAL_ENTITY maps to Company). */
const grainUiLabel = (grain: string): string =>
    (CONSTANTS.COA_SEGMENT_MAPPING as Record<string, { UI: string }>)[grain === 'LEGAL_ENTITY' ? 'COMPANY' : grain]?.UI ?? grain;

/**
 * A segment CDT / MDA output is selectable only when its segment matches the pool grain. A source's
 * `segmentType` is the enum form (e.g. COST_CENTER) but can also surface as the UI label, so compare
 * both. The CBP grain `LEGAL_ENTITY` corresponds to the segment-CDT `segmentType` `ENTITY`.
 */
const segmentMatchesGrain = (grain: string, segment?: string): boolean => {
    if (!segment) return true; // no segment metadata -> don't gate (keeps the picker usable)
    const seg = normSegment(segment);
    const aliases = [normSegment(grain), normSegment(grainUiLabel(grain))];
    if (grain === 'LEGAL_ENTITY') aliases.push(normSegment('ENTITY'));
    return aliases.includes(seg);
};

/**
 * Add-input dialog for the CBP builder Step 1. Consumes CommonBalancePoolBuilderContext (visibility,
 * edited row, grain, saving/error, services, persistColumns) rather than props. Two selections only —
 * the input type and the source object (a **segment** CDT table, or an MDA allocation output) — both at
 * the pool grain. For a segment CDT the columns are read from its definition (`GetSegmentDataTable`);
 * the merged column set is persisted via the shared `persistColumns` (UpdateCommonBalancePool).
 */
export default function AddCbpInputModal() {
    const { cbpBuilderState, cbpBuilderDispatch, services, persistColumns } = useContext(CommonBalancePoolBuilderContext);
    const { addModalVisible: visible, editingRow, saving, saveError: error, pool, segmentTables, segmentTablesLoading, segmentTablesError } = cbpBuilderState;
    const grain = pool?.grain ?? '';
    const isEditing = !!editingRow;
    // Stable key for the input being edited so the open/seed + preselect effects don't re-run on every
    // parent re-render, which would otherwise reset the user's in-progress selection.
    const editingKey = editingRow ? (editingRow.cols?.[0]?.columnId ?? editingRow.sourceType ?? 'edit') : 'add';

    const [inputType, setInputType] = useState<SelectProps.Option>(INPUT_TYPE_OPTIONS[0]);
    // Text shown in the source Autosuggest (§12.2 filterable combobox). The resolved source is DERIVED
    // from this text below (via a display→source map) so we key off the stable id, not a collidable label.
    const [sourceText, setSourceText] = useState('');
    const [validationError, setValidationError] = useState<string | undefined>(undefined);

    const isCdt = inputType.value === 'SEGMENT_CDT';

    // Segment CDTs (NOT CLI-keyed tables) come from the builder's shared reducer state — fetched once by
    // the builder, read here — so the picker doesn't fetch the list a second time. MDA sheets are fetched
    // for the MDA output type.
    const [sheetsQuery, setSheetsQuery] = useState('');
    const [sheetsResult, sheetsLoading, sheetsError] = services.multidimensionalAllocationService.listMultiAllocationSheets(
        sheetsQuery,
        'ACTIVE',
    );

    // Seed / reset each time the modal opens. Kick off the source fetch for the active input type.
    useEffect(() => {
        if (!visible) return;
        setValidationError(undefined);
        const type = editingRow?.sourceType ?? editingRow?.cols?.[0]?.sourceType ?? 'SEGMENT_CDT';
        setInputType(inputTypeOptionsFor(type).find((o) => o.value === type) ?? SEGMENT_CDT_OPTION);
        setSourceText('');
        setSheetsQuery(`cbp-input-${Date.now()}`);
        // Seed only on open (and when the edited input changes) — keyed on a stable primitive.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, editingKey]);

    // Options for the "source object" dropdown, grain-gated (mismatched entries disabled, per the mockup).
    const cdtOptions: SelectProps.Option[] = useMemo(
        () =>
            (segmentTables ?? []).map((t) => {
                const selectable = segmentMatchesGrain(grain, t.segmentType);
                return {
                    label: t.segmentType ? `${t.tableName} (${t.segmentType})` : String(t.tableName ?? ''),
                    value: t.tableId,
                    description: t.tableName,
                    disabled: !selectable,
                    labelTag: selectable ? undefined : 'not selectable',
                };
            }),
        [segmentTables, grain],
    );

    const mdaOptions: SelectProps.Option[] = useMemo(
        () =>
            (sheetsResult?.sheets ?? []).map((s) => {
                const selectable = segmentMatchesGrain(grain, s.segmentType);
                return {
                    label: s.segmentType ? `${s.sheetName} (${s.segmentType})` : s.sheetName,
                    value: s.sheetId,
                    description: s.sheetName,
                    disabled: !selectable,
                    labelTag: selectable ? undefined : 'not selectable',
                };
            }),
        [sheetsResult, grain],
    );

    const sourceOptions = isCdt ? cdtOptions : mdaOptions;
    const sourceLoading = isCdt ? !!segmentTablesLoading : sheetsLoading;

    // §12.2 "ratio-attach select" as an Autosuggest. Autosuggest shows (and returns) the option's
    // `value`, so build UNIQUE display strings — source labels can collide (same table/sheet name), and
    // keying the resolution by label would silently map a pick to the wrong id. Disambiguate a duplicated
    // label with its stable id, and resolve the picked text back to its source via that display map.
    const { sourceSuggestions, sourceByDisplay } = useMemo(() => {
        const labelCounts = new Map<string, number>();
        sourceOptions.forEach((o) => labelCounts.set(String(o.label), (labelCounts.get(String(o.label)) ?? 0) + 1));
        const suggestions: AutosuggestProps.Option[] = [];
        const byDisplay = new Map<string, SelectProps.Option>();
        sourceOptions.forEach((o) => {
            const label = String(o.label);
            const display = (labelCounts.get(label) ?? 0) > 1 ? `${label} (${o.value})` : label;
            suggestions.push({ value: display, description: o.description, disabled: o.disabled, labelTag: o.labelTag });
            byDisplay.set(display, o);
        });
        return { sourceSuggestions: suggestions, sourceByDisplay: byDisplay };
    }, [sourceOptions]);

    // Resolved selection is derived from the text (keyed by the unique display string), and never a
    // not-selectable option — so buildColumns always persists the correct, id-bearing source.
    const resolved = sourceByDisplay.get(sourceText);
    const selectedSource = resolved && !resolved.disabled ? resolved : null;

    // The selected segment table's column definitions (columnId + displayName + dataType) — read on Add.
    const [tableDetail, detailLoading, detailError] = services.segmentDataTablesService.getSegmentDataTable(
        isCdt && selectedSource?.value ? String(selectedSource.value) : undefined,
    );
    // Surface a failed source-list / detail load so it's distinguishable from a genuinely empty result.
    const sourceError = isCdt ? (segmentTablesError || detailError) : sheetsError;

    // Preselect the source when editing (the row already references a table/sheet) — set the text to that
    // source's unique display string; the derived selection takes it from there.
    useEffect(() => {
        if (!visible || !editingRow) return;
        const first = editingRow.cols[0];
        const value = first?.sourceRef?.cdtTableId ?? first?.sourceRef?.mdaSheetId;
        // Only seed while the text is still empty (it's reset to '' on open) so a later source-list
        // resolve or input-type change doesn't clobber a selection the user made mid-edit.
        if (value && !sourceText) {
            const entry = [...sourceByDisplay.entries()].find(([, s]) => s.value === value);
            if (entry) setSourceText(entry[0]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, editingKey, sourceByDisplay]);

    const onInputTypeChange = (option: SelectProps.Option) => {
        setInputType(option);
        setSourceText('');
        setValidationError(undefined);
    };

    // §12.2 inline "Create new…" link target — the module where this source type is created.
    const createNewHref = isCdt ? '/calculations/customDataTables' : '/calculations/multidimensionalAllocation';

    const handleCancel = () => cbpBuilderDispatch(ACTIONS.CLOSE_INPUT_MODAL.withPayload(undefined));

    const buildColumns = (): CommonBalancePoolColumn[] | undefined => {
        if (!selectedSource?.value) {
            setValidationError(isCdt ? 'Select a segment CDT table.' : 'Select an MDA allocation output.');
            return undefined;
        }

        if (isCdt) {
            const table = tableDetail?.table;
            // One CBP INPUT column per NUMERIC segment-table column definition — keyed <tableId>:<columnId>,
            // matching the stored column_id shape. TEXT columns (e.g. a classification/mapping) can't be a
            // numeric balance-pool input, so they're excluded rather than coerced to NUMBER.
            const cols = (table?.columns ?? []).filter(
                (c) => c.columnId && (c.dataType === 'NUMBER' || c.dataType === 'PERCENTAGE'),
            );
            if (cols.length === 0) {
                setValidationError('This table has no numeric columns to add.');
                return undefined;
            }
            // columnOrder is assigned once at the handleAdd merge site (offset by the current max), not
            // here — a per-batch index would restart at 0 and collide with existing columns' orders.
            return cols.map((c) => ({
                columnId: `${selectedSource.value}:${c.columnId}`,
                displayName: c.displayName || String(c.columnId),
                kind: 'INPUT',
                dataType: c.dataType === 'PERCENTAGE' ? 'PERCENTAGE' : 'NUMBER',
                sourceType: 'SEGMENT_CDT',
                sourceRef: {
                    cdtTableId: String(selectedSource.value),
                    cdtTableName: table?.tableName ?? String(selectedSource.description ?? ''),
                    cdtColumnId: c.columnId,
                },
            }));
        }

        // MDA allocation output — a single output column referencing the sheet.
        const sheet = (sheetsResult?.sheets ?? []).find((s) => s.sheetId === selectedSource.value);
        return [
            {
                columnId: `${selectedSource.value}:output`,
                displayName: `${sheet?.sheetName ?? selectedSource.description} output`,
                kind: 'INPUT',
                dataType: 'NUMBER',
                // columnOrder is assigned at the handleAdd merge site (see the CDT branch note above).
                sourceType: 'MDA_OUTPUT',
                sourceRef: {
                    mdaSheetId: selectedSource.value,
                    mdaSheetName: sheet?.sheetName ?? selectedSource.description,
                    mdaSheetVersion: sheet?.sheetVersion,
                },
            },
        ];
    };

    // Merge the built INPUT columns into the pool (append when adding; replace the edited source's
    // columns when editing) and persist via the shared choke point; close on success.
    const handleAdd = async () => {
        const newColumns = buildColumns();
        if (!newColumns) return;
        const existing = pool?.columns ?? [];
        // Reject a source already used by another input row — on both the add and edit paths. Duplicate
        // INPUT columnIds would collide, and persistColumns only guards dangling refs, not duplicate ids,
        // which collapses formula references (dependsOn / nameById) and React keys. On the edit path,
        // exclude the row being edited (its own source key) so re-saving the same source is allowed.
        const newSourceKey = sourceKeyOf(newColumns[0]);
        const collides = existing.some(
            (col) => col.kind === 'INPUT'
                && sourceKeyOf(col) === newSourceKey
                && sourceKeyOf(col) !== editingRow?.sourceKey,
        );
        if (collides) {
            setValidationError('This source has already been added.');
            return;
        }
        // Columns being kept: everything except the edited source's own columns (which this batch
        // replaces) on the edit path, or all existing columns on the add path.
        const retained = editingRow
            ? existing.filter((col) => !(col.kind === 'INPUT' && sourceKeyOf(col) === editingRow.sourceKey))
            : existing;
        // Assign columnOrder once here — the single merge site — so both the CDT and MDA source types get
        // contiguous, non-colliding orders. Append the new batch after the retained max; restarting the
        // index at 0 per batch (the old buildColumns behaviour) collided under the CbpResultsSection
        // `columnOrder` sort and made the results-grid column order indeterminate.
        const base = retained.reduce((max, col) => Math.max(max, col.columnOrder ?? -1), -1) + 1;
        const ordered = newColumns.map((col, idx) => ({ ...col, columnOrder: base + idx }));
        const nextColumns = [...retained, ...ordered];
        const ok = await persistColumns(nextColumns);
        if (ok) cbpBuilderDispatch(ACTIONS.CLOSE_INPUT_MODAL.withPayload(undefined));
    };

    // "Reading columns" note while the segment-table detail fetch is in flight.
    const detailPending = isCdt && !!selectedSource && detailLoading;
    // Add must also wait until the loaded detail actually corresponds to the current source — the fetch
    // is driven by the getSegmentDataTable hook, so right after switching tables the detail can still hold
    // the previous table. Gate on the loaded table id matching the selection AND not loading.
    const detailReady = !!selectedSource && !detailLoading && tableDetail?.table?.tableId === String(selectedSource.value);
    const addDisabled = !!saving || !selectedSource || (isCdt && !detailReady);

    return (
        <Modal
            visible={visible}
            onDismiss={(event) => {
                event.detail.reason !== 'overlay' && handleCancel();
            }}
            header={isEditing ? 'Edit input' : 'Add input'}
            closeAriaLabel="Close modal"
            size="medium"
            footer={
                <Box float="right">
                    <SpaceBetween direction="horizontal" size="xs">
                        <Button variant="link" onClick={handleCancel} disabled={saving}>Cancel</Button>
                        <Button variant="primary" onClick={handleAdd} loading={saving} disabled={addDisabled}>
                            {isEditing ? 'Save input' : 'Add input'}
                        </Button>
                    </SpaceBetween>
                </Box>
            }
        >
            <SpaceBetween direction="vertical" size="m">
                <Box color="text-body-secondary">
                    Add columns to the CBP at the {grainUiLabel(grain)} grain. Columns are read from the selected
                    source; you don&apos;t enter them by hand.
                </Box>

                <FormField label="Input type">
                    <Select
                        selectedOption={inputType}
                        onChange={({ detail }) => onInputTypeChange(detail.selectedOption)}
                        options={inputTypeOptionsFor(editingRow?.sourceType ?? editingRow?.cols?.[0]?.sourceType)}
                    />
                </FormField>

                <FormField
                    label={isCdt ? 'Select segment CDT table' : 'Select MDA allocation output'}
                    description={
                        isCdt
                            ? `Segment CDTs come from the CDT module. Only tables at this CBP's grain (${grainUiLabel(grain)}) are selectable.`
                            : `Only MDA outputs at this CBP's grain (${grainUiLabel(grain)}) are selectable.`
                    }
                >
                    <SpaceBetween direction="vertical" size="xs">
                        <Autosuggest
                            value={sourceText}
                            onChange={({ detail }) => setSourceText(detail.value)}
                            onSelect={({ detail }) => setSourceText(detail.value)}
                            options={sourceSuggestions}
                            filteringType="auto"
                            statusType={sourceLoading ? 'loading' : 'finished'}
                            loadingText="Loading…"
                            empty={isCdt ? 'No segment CDT tables found.' : 'No MDA outputs found.'}
                            placeholder={isCdt ? 'Choose a segment CDT table' : 'Choose an MDA allocation output'}
                            enteredTextLabel={(v) => `Use: ${v}`}
                            ariaLabel={isCdt ? 'Segment CDT table' : 'MDA allocation output'}
                        />
                        <Link href={createNewHref}>
                            {isCdt ? 'Create new segment CDT…' : 'Create new MDA allocation output…'}
                        </Link>
                    </SpaceBetween>
                </FormField>

                {detailPending && (
                    <Box color="text-body-secondary">
                        <SpaceBetween direction="horizontal" size="xs">
                            <Spinner size="normal" />
                            <span>Reading columns from the selected table…</span>
                        </SpaceBetween>
                    </Box>
                )}

                {(validationError || sourceError || error) && (
                    <Alert
                        type="error"
                        dismissible={!!validationError}
                        header={sourceError && !validationError && !error ? 'Could not load sources' : 'Could not add input'}
                        onDismiss={() => setValidationError(undefined)}
                    >
                        {withNames(validationError || sourceError || error, pool?.columns, pool)}
                    </Alert>
                )}
            </SpaceBetween>
        </Modal>
    );
}
